<!-- Loading state -->
<div *ngIf="isLoading && !editMode" class="loading-container">
  <p>Loading...</p>
  <!-- You can add a spinner here -->
</div>

<!-- Initial Selection Screen (only for new uploads) -->
<div
  *ngIf="!selection && !editMode && !isLoading && !showUpgradePrompt"
  class="selection-container"
>
  <div class="upload-usage-info">
    <p *ngIf="uploadLimit !== 'unlimited'">
      Uploads used: {{ currentUploads }} / {{ uploadLimit }}
    </p>
    <p *ngIf="uploadLimit === 'unlimited'">You have unlimited uploads.</p>
  </div>

  <h2>What would you like to upload?</h2>
  <div class="selection-buttons">
    <button
      mat-raised-button
      color="primary"
      class="music-upload-btn"
      (click)="selectUpload('music')"
    >
      <div class="icon-top-wrapper">
        <mat-icon>music_note</mat-icon>
        <span>Upload Music</span>
      </div>
    </button>
    <button mat-raised-button color="accent" (click)="selectUpload('video')">
      <div class="icon-top-wrapper">
        <mat-icon>videocam</mat-icon>
        <span>Upload Video</span>
      </div>
    </button>
  </div>
</div>

<!-- Upgrade Prompt -->
<div *ngIf="showUpgradePrompt" class="upgrade-prompt">
  <h3>You've reached your upload limit!</h3>
  <p>
    <ng-container [ngSwitch]="currentUser?.creatorType">
      <span *ngSwitchCase="'creator'">Creators on a free plan</span>
      <span *ngSwitchCase="'verified'">Verified creators</span>
      <span *ngSwitchDefault>Users with your account type</span>
    </ng-container>
    can only upload up to {{ uploadLimit }} releases. Please upgrade your
    account to upload more music.
  </p>
  <div class="form-actions">
    <button mat-raised-button color="accent" (click)="navigateToUpgrade()">
      Upgrade Now
    </button>
    <button mat-button (click)="backToSelection()">Back</button>
  </div>
</div>

<!-- Music Upload Form (for new music uploads or editing) -->
<div *ngIf="selection === 'music' || editMode">
  <div class="upload-container" *ngIf="!releaseNameSet && !editMode">
    <h2>Create a new release</h2>
    <form
      [formGroup]="releaseForm"
      (ngSubmit)="setReleaseName()"
      class="upload-form"
    >
      <div class="form-section">
        <label for="release-name">Release Name:</label>
        <input
          id="release-name"
          formControlName="releaseName"
          placeholder="Release Name"
        />
      </div>
      <div class="form-section">
        <label for="release-artist">Artist Name:</label>
        <input
          id="release-artist"
          formControlName="releaseArtist"
          placeholder="Artist Name"
          *ngIf="!isLabelAccount"
        />
        <input
          *ngIf="isLabelAccount"
          value="Set artist on next screen"
          disabled
        />
      </div>
      <div class="form-actions">
        <button
          mat-raised-button
          color="primary"
          type="submit"
          [disabled]="releaseForm.invalid"
        >
          Continue
        </button>
        <button mat-button type="button" (click)="backToSelection()">
          Back
        </button>
      </div>
    </form>
  </div>

  <div *ngIf="releaseNameSet || editMode" class="upload-container">
    <div class="form-actions page-actions">
      <button
        mat-raised-button
        color="primary"
        (click)="uploadAlbum()"
        [disabled]="isUploadingAlbum"
      >
        <span *ngIf="!isUploadingAlbum">Save</span>
        <span *ngIf="isUploadingAlbum">{{
          editMode ? "Saving..." : "Creating..."
        }}</span>
      </button>
      <button mat-button (click)="cancel()">Cancel</button>
    </div>
    <h2>{{ editMode ? "Edit your release" : "Upload your release" }}</h2>
    <div class="music-upload-layout">
      <div class="album-details-column">
        <form
          [formGroup]="albumForm"
          (ngSubmit)="uploadAlbum()"
          class="upload-form"
        >
          <div class="form-section">
            <label>Album Art:</label>
            <div class="file-input-wrapper">
              <input
                type="file"
                (change)="onArtSelected($event)"
                accept="image/*"
              />
              <img
                [src]="imagePreview"
                *ngIf="imagePreview"
                class="image-preview"
              />
            </div>
          </div>

          <div class="form-section">
            <label>Album Name:</label>
            <input formControlName="name" placeholder="Album Name" />
          </div>
          <div class="form-section">
            <label>Album Artist:</label>
            <input *ngIf="!isLabelAccount" formControlName="artist" />
            <select *ngIf="isLabelAccount" formControlName="artist">
              <option value="" disabled>Select an artist</option>
              <option
                *ngFor="let artist of currentUser.labelArtists"
                [value]="artist.name"
              >
                {{ artist.name }}
              </option>
            </select>
          </div>
          <div class="form-section">
            <label>Spotify Album URI:</label>
            <input formControlName="uri" placeholder="Optional" />
          </div>
          <div class="form-section">
            <label>Apple Pre-save Link:</label>
            <input formControlName="appleSave" placeholder="Optional" />
          </div>
          <div class="form-section">
            <label>Release Type:</label>
            <select formControlName="type">
              <option value="" disabled selected>Release Type</option>
              <option value="EP">EP</option>
              <option value="Single">Single</option>
              <option value="unreleased EP">Unreleased EP</option>
              <option value="unreleased Single">Unreleased Single</option>
              <option value="topPick">Top Pick</option>
              <option value="liveShow">Live Show</option>
            </select>
          </div>
          <div class="form-section">
            <label>Release Date:</label>
            <input
              formControlName="released"
              type="date"
              placeholder="Release Date"
            />
          </div>
          <div class="form-section">
            <mat-slide-toggle formControlName="showing">
              Slide me to put in draft mode
            </mat-slide-toggle>
          </div>
          <div class="form-section">
            <label>Price</label>
            <input
              type="text"
              formControlName="price"
              placeholder="e.g., 9.99"
            />
          </div>
          <div class="form-section">
            <mat-slide-toggle formControlName="membersOnly"
              >Members Only</mat-slide-toggle
            >
          </div>
          <div class="form-section" *ngIf="albumForm.get('membersOnly').value">
            <label>Required Membership:</label>
            <select formControlName="tier">
              <option value="" disabled>Select a tier</option>
              <option
                *ngFor="let member of currentUser?.memberships"
                [value]="member.tier"
              >
                {{ member.tier }} - ${{ member.price }}
              </option>
            </select>
            <p *ngIf="albumForm.get('level').value" class="help-text">
              Selected Level: {{ albumForm.get("level").value }}
            </p>
          </div>
        </form>
      </div>
      <div class="track-details-column">
        <!-- Track Management Section -->
        <div class="track-management">
          <div class="form-actions">
            <button type="button" (click)="toggleTrackForm()">
              {{ showTrackForm ? "Close Track Form" : "Add Track" }}
            </button>
          </div>

          <!-- Track Form (collapsible) -->
          <form
            *ngIf="showTrackForm"
            [formGroup]="trackForm"
            (ngSubmit)="saveTrack()"
            class="upload-form track-form"
          >
            <h3>{{ trackForm.value.id ? "Edit Track" : "Add New Track" }}</h3>
            <div class="form-section">
              <label>Track Title:</label>
              <input formControlName="title" placeholder="Track Title" />
            </div>
            <div class="form-section">
              <label>Track Artists:</label>
              <div class="artist-chips-container" formArrayName="artists">
                <div
                  *ngFor="let artist of trackArtists.controls; let i = index"
                  [formGroupName]="i"
                  class="artist-chip"
                >
                  <ng-container *ngIf="i === 0">
                    <span *ngIf="!isLabelAccount">{{ artist.value.name }}</span>
                    <select
                      *ngIf="isLabelAccount"
                      class="primary-artist-select"
                      (change)="updatePrimaryTrackArtist($event.target.value)"
                    >
                      <option
                        *ngFor="let labelArtist of currentUser.labelArtists"
                        [value]="labelArtist.id"
                        [selected]="labelArtist.id === artist.value.id"
                      >
                        {{ labelArtist.name }}
                      </option>
                    </select>
                  </ng-container>
                  <ng-container *ngIf="i > 0">
                    <span>{{ artist.value.name }}</span>
                    <button
                      type="button"
                      class="remove-artist-btn"
                      (click)="removeTrackArtist(i)"
                    >
                      &times;
                    </button>
                  </ng-container>
                </div>
              </div>
              <button
                type="button"
                (click)="showArtistSearch = !showArtistSearch"
              >
                Add Featured Artist
              </button>
              <div *ngIf="showArtistSearch" class="artist-search-container">
                <input
                  [formControl]="artistSearchQuery"
                  (keyup)="searchArtists()"
                  placeholder="Search for artist..."
                />
                <div
                  *ngFor="let artist of artistSearchResults"
                  class="search-result"
                  (click)="addTrackArtist(artist)"
                >
                  <img
                    *ngIf="artist.avatarUrl"
                    [src]="artist.avatarUrl"
                    class="artist-avatar-thumbnail"
                    alt="artist avatar"
                  />
                  <mat-icon
                    *ngIf="!artist.avatarUrl"
                    class="artist-avatar-thumbnail"
                    >account_circle</mat-icon
                  >
                  <span>{{ artist.name }}</span>
                </div>
                <div
                  *ngIf="showCreateNewArtistOption"
                  class="search-result create-new"
                  (click)="createNewArtist(artistSearchQuery.value)"
                >
                  Add "{{ artistSearchQuery.value }}" as a new artist
                </div>
              </div>
            </div>
            <div class="form-section">
              <label>Spotify Track URI:</label>
              <input formControlName="uri" placeholder="Spotify URI" />
            </div>
            <div class="form-section">
              <label>Apple Music Link:</label>
              <input formControlName="apple" placeholder="Apple Music Link" />
            </div>
            <div class="form-section">
              <label>Written By:</label>
              <input formControlName="writers" placeholder="Anne Stead" />
            </div>
            <div class="form-section">
              <label>Produced By:</label>
              <input formControlName="producers" placeholder="Tk808, Juicy J" />
            </div>
            <div class="form-section">
              <label>Engineered By:</label>
              <input formControlName="engineers" placeholder="KY Engineering" />
            </div>
            <div class="form-section">
              <label>Lyrics:</label>
              <textarea
                formControlName="lyrics"
                placeholder="Track Lyrics"
              ></textarea>
            </div>
            <div class="form-section">
              <label>Price</label>
              <input
                type="text"
                formControlName="price"
                placeholder="e.g., 1.29"
              />
            </div>
            <div class="form-section">
              <mat-slide-toggle formControlName="membersOnly"
                >Members Only</mat-slide-toggle
              >
            </div>
            <div
              class="form-section"
              *ngIf="trackForm.get('membersOnly').value"
            >
              <label>Required Membership:</label>
              <select formControlName="tier">
                <option value="" disabled>Select a tier</option>
                <option
                  *ngFor="let member of currentUser?.memberships"
                  [value]="member.tier"
                >
                  {{ member.tier }} ({{ member.level }}) - ${{ member.price }}
                </option>
              </select>
              <p *ngIf="trackForm.get('level').value" class="help-text">
                Selected Level: {{ trackForm.get("level").value }}
              </p>
            </div>
            <div class="form-section">
              <label>Upload Track:</label>
              <input
                type="file"
                (change)="onTrackSelected($event, trackIndex)"
                accept="audio/*"
              />
              <p class="help-text">(.m4a files ONLY)</p>
            </div>
            <mat-progress-bar
              *ngIf="progress > 0"
              mode="determinate"
              [value]="progress"
            ></mat-progress-bar>
            <div class="form-actions">
              <button
                mat-raised-button
                color="primary"
                type="submit"
                [disabled]="isSavingTrack"
              >
                <span *ngIf="!isSavingTrack">Save Track</span>
                <span *ngIf="isSavingTrack">Saving...</span>
              </button>
              <button mat-button type="button" (click)="toggleTrackForm()">
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div class="track-list" *ngIf="songs && songs.length > 0">
          <h2>Track List</h2>
          <div cdkDropList (cdkDropListDropped)="drop($event)">
            <div
              class="trax"
              *ngFor="let track of songs; let i = index"
              cdkDrag
            >
              <span class="track-info">
                {{ i + 1 }}. {{ getArtistNames(track.artists) }} -
                {{ track.title }}
              </span>
              <div class="track-meta">
                <span class="track-duration" *ngIf="track.duration">{{
                  track.duration
                }}</span>
                <button (click)="editTrack(i, track)">Edit</button>
                <button (click)="removeTrack(i)">Remove</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Video Upload Form -->
<div *ngIf="selection === 'video'" class="upload-container">
  <h2>Upload Video</h2>
  <form
    [formGroup]="uploadVidForm"
    (ngSubmit)="onVidSubmit()"
    class="upload-form"
  >
    <div class="form-section">
      <label>Video Title:</label>
      <input type="text" formControlName="title" placeholder="Video Title" />
    </div>
    <div class="form-section">
      <label>Video Type:</label>
      <select formControlName="type">
        <option value="" disabled>Select a video type</option>
        <option value="Music Video">Music Video</option>
        <option value="Podcast">Podcast</option>
        <option value="Interview">Interview</option>
        <option value="Behind the Scenes">Behind the Scenes</option>
        <option value="Live Performance">Live Performance</option>
      </select>
    </div>
    <div class="form-section">
      <label>Description:</label>
      <textarea
        formControlName="description"
        placeholder="Add a description for your video..."
      ></textarea>
    </div>
    <div class="form-section">
      <label>YouTube Link (optional):</label>
      <input
        type="url"
        formControlName="youtubeLink"
        placeholder="https://www.youtube.com/watch?v=..."
      />
    </div>
    <div class="form-section">
      <label>Video File:</label>
      <input type="file" (change)="onFileChange($event)" accept="video/*" />
    </div>
    <div class="form-section">
      <label>Video Thumbnail</label>
      <p class="help-text">
        A thumbnail will be auto-generated from the video. You can upload a
        custom image to override it.
      </p>
      <div class="file-input-wrapper">
        <input
          type="file"
          (change)="onThumbnailSelected($event)"
          accept="image/*"
        />
        <img
          *ngIf="thumbnailPreview"
          [src]="thumbnailPreview"
          class="image-preview"
          alt="Video thumbnail preview"
        />
      </div>
    </div>
    <div class="form-section date-fields">
      <div>
        <label>Release Date:</label>
        <input type="date" formControlName="releaseDate" />
      </div>
      <div>
        <label>DSP Release Date:</label>
        <input type="date" formControlName="dspReleaseDate" />
        <div class="checkbox-wrapper">
          <input
            type="checkbox"
            id="isDspDateSame"
            formControlName="isDspDateSame"
          />
          <label for="isDspDateSame">Same as Release Date</label>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label>Price</label>
      <input type="text" formControlName="price" placeholder="e.g., 2.99" />
    </div>
    <div class="form-section">
      <mat-slide-toggle formControlName="membersOnly"
        >Members Only</mat-slide-toggle
      >
    </div>
    <div class="form-section" *ngIf="uploadVidForm.get('membersOnly').value">
      <label>Required Membership:</label>
      <select formControlName="tier">
        <option value="" disabled>Select a tier</option>
        <option
          *ngFor="let member of currentUser?.memberships"
          [value]="member.tier"
        >
          {{ member.tier }} - ${{ member.price }}
        </option>
      </select>
      <p *ngIf="uploadVidForm.get('level').value" class="help-text">
        Selected Level: {{ uploadVidForm.get("level").value }}
      </p>
    </div>
    <div class="form-section">
      <mat-slide-toggle formControlName="showing">
        Status: {{ uploadVidForm.get("showing").value ? "Active" : "Draft" }}
      </mat-slide-toggle>
    </div>
    <mat-progress-bar
      *ngIf="uploadProgress > 0"
      mode="determinate"
      [value]="uploadProgress"
    ></mat-progress-bar>
    <div class="form-actions">
      <button
        mat-raised-button
        color="primary"
        type="submit"
        [disabled]="!uploadVidForm.valid || isUploadingVideo"
      >
        <span *ngIf="!isUploadingVideo">Upload Video</span>
        <span *ngIf="isUploadingVideo">Uploading...</span>
      </button>
      <button mat-button type="button" (click)="backToSelection()">Back</button>
    </div>
  </form>
</div>
